import { Check, Square, Lock } from 'lucide-react';
import { FinancialData } from './types';
import { useEffect, useMemo } from 'react';
import React from 'react';
import { useContext } from 'react';
import { UserContext } from '../../context/UserContext';

interface FinancialMetricsTableProps {
  financialData: FinancialData;
  selectedMetrics: string[];
  onMetricToggle: (metricId: string) => void;
  isHistorical?: boolean;
  title: string;
}

export function FinancialMetricsTable({
  financialData,
  selectedMetrics,
  onMetricToggle,
  isHistorical = false,
  title = "Financial Metrics"
}: FinancialMetricsTableProps) {
  const [periods, setPeriods] = React.useState<string[]>([]);
  const [heatmapOn, setHeatmapOn] = React.useState<boolean>(false); // sensitivity: 15/30/50
  const [heatScale, setHeatScale] = React.useState<number>(50); // sensitivity: 15/30/50
  const [hoverCol, setHoverCol] = React.useState<number | null>(null);
  const { userData } = useContext(UserContext) || {};
  const isLoggedIn = typeof window !== 'undefined' && Boolean(sessionStorage.getItem('STOCK_AUTH_TOKEN'));
  const hasAccess = isLoggedIn && (userData?.planActive === true || userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing');

  const getNumeric = (v: any): number | null => {
    if (typeof v === 'number') return v;
    if (v && typeof v.value === 'number') return v.value;
    return null;
  };

  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
  // const periods = financialData?.periods.reverse() || [];
  const metrics = financialData?.metrics || [];

  // Helper: strip HTML tags to produce clean tooltip text
  const stripHtml = (html: string): string => {
    if (!html) return '';
    const tmp = typeof window !== 'undefined' ? window.document.createElement('div') : null;
    if (!tmp) return html;
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  };
  
  useEffect(() => {
    if (financialData?.periods && financialData?.periods.length > 0) {
      setPeriods([]);
      setPeriods(financialData.periods);
    }
  }, [financialData]);

  const unlockedCount = 10;
  const unlockedIndices = useMemo(() => new Set(Array.from({ length: Math.min(unlockedCount, periods.length) }, (_, i) => i)), [periods, unlockedCount]);

  // Consider a metric "visible" only if at least one period has a non-null numeric value
  const metricHasAnyValue = (m: any): boolean => {
    const ps = m?.periods || [];
    return ps.some((p: any) => getNumeric(p?.value) !== null);
  };

  // Filter out metrics that are entirely null to avoid showing labels with no data
  const filteredMetrics = useMemo(() => (metrics || []).filter(metricHasAnyValue), [metrics]);

  // Group metrics by category for sectioned display
  const categoryOrder = useMemo(() => {
    const set = new Set<string>();
    (filteredMetrics || []).forEach((m: any) => set.add(m?.category || 'General'));
    return Array.from(set);
  }, [filteredMetrics]);
  const grouped = useMemo(() => {
    const map: Record<string, any[]> = {};
    categoryOrder.forEach(c => { map[c] = []; });
    (filteredMetrics || []).forEach((m: any) => {
      const c = m?.category || 'General';
      if (!map[c]) map[c] = [];
      map[c].push(m);
    });
    return map;
  }, [filteredMetrics, categoryOrder]);
  const categoryColor = (cat: string) => {
    const key = (cat || '').toLowerCase();
    if (key.includes('mandatory') || key.includes('essential')) return 'from-yellow-600/40 to-yellow-500/20';
    if (key.includes('growth') || key.includes('rev')) return 'from-cyan-600/40 to-cyan-500/20';
    if (key.includes('margin') || key.includes('profit')) return 'from-amber-700/40 to-amber-500/20';
    if (key.includes('valuation') || key.includes('ratio')) return 'from-purple-700/40 to-purple-500/20';
    return 'from-gray-700/40 to-gray-600/20';
  };

  // Determine whether "higher is better" or "lower is better" for a metric using lightweight heuristics.
  // Returns 1 for higher-is-better, -1 for lower-is-better, 0 for neutral/unknown.
  const getBenefitPolarity = (metric: any): number => {
    const name = (metric?.metricName || '').toLowerCase();
    const label = (metric?.label || '').toLowerCase();
    const text = `${name} ${label}`;
    // Keywords where LOWER is better
    const lowerBetter = [
      'expense', 'expenses', 'cost', 'cogs', 'selling and marketing', 'sales and marketing', 'marketing', 's&m', 'sga', 'general and administrative', 'administrative', 'stock-based compensation', 'stock based compensation', 'diluted shares', 'shares outstanding', 'share count', 'debt', 'liabilities', 'interest expense', 'capex', 'capital expenditures', 'days sales outstanding', 'inventory days', 'receivable days'
    ];
    // Keywords where HIGHER is better
    const higherBetter = [
      'revenue', 'sales', 'income', 'profit', 'margin', 'gross margin', 'operating margin', 'free cash flow', 'cash', 'assets', 'return on', 'roe', 'roa'
    ];
    if (lowerBetter.some(k => text.includes(k))) return -1;
    if (higherBetter.some(k => text.includes(k))) return 1;
    return 0; // neutral/unknown
  };

  const getDeltaPct = (metric: any, i: number, ps: string[]) => {
    // Prefer appendString like "+6.7%" if present; otherwise compute from previous period
    const curr = metric?.periods?.find((x: any) => x.year.year === ps[i]);
    const val = curr?.value;
    const app = val?.appendString as string | undefined;
    if (app && app !== '%') {
      const m = app.match(/-?\d+(?:\.\d+)?/);
      if (m) return parseFloat(m[0]);
    }
    if (i > 0) {
      const prev = metric?.periods?.find((x: any) => x.year.year === ps[i - 1]);
      const currNum = getNumeric(val);
      const prevNum = getNumeric(prev?.value);
      if (typeof currNum === 'number' && typeof prevNum === 'number' && prevNum !== 0) {
        return ((currNum - prevNum) / Math.abs(prevNum)) * 100;
      }
    }
    return 0;
  };

  return <div className="overflow-x-auto scrollbar-hide" style={{ position: 'relative', zIndex: 1 }}>
    <table className="w-full text-left border-separate border-spacing-0">
      <thead>
        <tr className="bg-theme-secondary text-theme-primary">
          <th className="w-64 p-2 border-b border-theme-tertiary text-xs font-medium sticky left-0 top-0 z-40 bg-theme-secondary">
            <div className="flex items-center justify-between gap-2">
              <span>{title}</span>
              {/* Compact Heatmap toggle */}
              <button
                type="button"
                title={heatmapOn ? 'Heatmap: On' : 'Heatmap: Off'}
                onClick={() => setHeatmapOn(v => !v)}
                className={`shrink-0 inline-flex items-center rounded-full border border-theme-tertiary transition-colors ${heatmapOn ? 'bg-green-600/30 text-green-300' : 'bg-theme-tertiary/50 text-theme-secondary'} px-1.5 py-0.5 text-[10px] leading-none`}
              >
                HM
                <span className={`ml-1 inline-block h-3 w-5 rounded-full relative bg-theme-secondary/40`}> 
                  <span className={`absolute top-0.5 h-2 w-2 rounded-full transition-all ${heatmapOn ? 'bg-green-400 right-0.5' : 'bg-gray-400 left-0.5'}`}></span>
                </span>
              </button>
              {heatmapOn && (
                <div className="hidden sm:flex items-center gap-2 ml-2">
                  <div className="h-2 w-16 rounded bg-gradient-to-r from-red-500/50 via-transparent to-green-500/50" title={`Heatmap scale: ±${heatScale}%`} />
                  <select
                    value={heatScale}
                    onChange={(e) => setHeatScale(parseInt(e.target.value, 10))}
                    className="bg-theme-tertiary border border-theme-tertiary rounded px-1 py-0.5 text-[10px] text-theme-primary"
                    title="Adjust heatmap sensitivity"
                  >
                    <option value={15}>15%</option>
                    <option value={30}>30%</option>
                    <option value={50}>50%</option>
                  </select>
                </div>
              )}
            </div>
          </th>
          {periods?.map((period, index) => <th
            key={`${period}-${index}`}
            className={`p-2 border-b border-theme-tertiary text-center text-xs font-medium sticky top-0 z-30 bg-theme-secondary text-theme-secondary`}
            onMouseEnter={() => setHoverCol(index)}
            onMouseLeave={() => setHoverCol((c) => (c === index ? null : c))}
          >
            {period}
          </th>)}
        </tr>
      </thead>
      <tbody>
        {categoryOrder.map((cat) => (
          <React.Fragment key={cat}>
            {/* Category Header Row */}
            <tr>
              <td colSpan={1 + (periods?.length || 0)} className="p-0">
                <div className={`flex items-center justify-between px-3 py-2 border-y border-theme-tertiary bg-gradient-to-r ${categoryColor(cat)}`}>
                  <div className="flex items-center gap-2">
                    <span className="uppercase tracking-wide text-[11px] font-semibold text-theme-primary/90 drop-shadow">{cat || 'General'}</span>
                    <span className="text-[10px] text-theme-primary/70">{grouped[cat]?.length ?? 0} metrics</span>
                  </div>
                  <button
                    type="button"
                    className="text-[10px] px-2 py-0.5 rounded bg-black/20 text-theme-primary/80 border border-white/10 hover:bg-black/30"
                    onClick={() => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }))}
                    aria-expanded={!collapsed[cat]}
                    aria-controls={`cat-${cat}`}
                  >
                    {collapsed[cat] ? 'Expand' : 'Collapse'}
                  </button>
                </div>
              </td>
            </tr>
            {/* Category Metrics */}
            {!collapsed[cat] && grouped[cat]?.map((metric: any, index: number) => {
              const isSelected = selectedMetrics.includes(metric?.metricName);
              return <tr key={metric?.metricName} id={`cat-${cat}`} className={`transition-colors border-b border-theme-tertiary hover:bg-theme-tertiary/20`}>
                <td className="p-2 border-b border-theme-tertiary sticky left-0 z-10 bg-theme-secondary">
                  <div className="flex items-center gap-2 max-w-[22rem]">
                    {isHistorical && <div className={`shrink-0 mr-1 rounded flex items-center justify-center cursor-pointer border-b border-theme-secondary ${isSelected ? 'bg-purple-500' : 'bg-theme-tertiary'}`} onClick={() => onMetricToggle(metric.metricName)}>
                      {isSelected && <Check size={18} className="text-theme-primary" />}
                      {!isSelected && <Square size={20} className="text-theme-tertiary" />}
                    </div>}
                    <span
                      className={`flex-1 block truncate text-xs ${metric.isPercentage ? 'italic text-theme-secondary' : 'text-theme-primary'}`}
                      title={stripHtml(metric.label)}
                      aria-label={stripHtml(metric.label)}
                      dangerouslySetInnerHTML={{ __html: metric.label }}
                    ></span>
                  </div>
                </td>
                {periods?.map((period, i) => {
                  const cell = metric.periods?.find((p:any) => p.year.year === period);
                  const value = cell?.value;
                  const formattedValue = (cell && (cell.formattedValue || value?.formattedValue)) || '';
                  const rawDeltaPct = getDeltaPct(metric, i, periods);
                  const polarity = getBenefitPolarity(metric);
                  const effectiveDelta = polarity === 0 ? 0 : rawDeltaPct * polarity;
                  const intensity = Math.min(1, Math.abs(effectiveDelta) / heatScale);
                  const bg = heatmapOn
                    ? (effectiveDelta > 0
                        ? `rgba(16,185,129, ${0.12 + intensity * 0.18})`
                        : (effectiveDelta < 0
                            ? `rgba(239,68,68, ${0.12 + intensity * 0.18})`
                            : 'transparent'))
                    : 'transparent';
                  const neutralizedHint = polarity === 0 ? 'neutral' : (polarity > 0 ? 'higher is better' : 'lower is better');
                  const tooltip = `${formattedValue} — ${rawDeltaPct.toFixed(1)}% YoY (${neutralizedHint})`;
                  const locked = !hasAccess && !unlockedIndices.has(i);
                  return <td key={`${metric?.metricName}-${period}-${i}`} className={`p-2 border-b border-theme-tertiary text-center relative ${hoverCol === i ? 'bg-theme-tertiary/10' : ''}`} style={{ backgroundColor: bg }} title={locked ? '' : tooltip}>
                    {locked ? (
                      <div className="flex items-center justify-center h-full">
                        <Lock size={14} className="text-theme-tertiary" />
                      </div>
                    ) : (
                      <div className={`text-xs ${heatmapOn ? (effectiveDelta > 0 ? 'text-green-400' : effectiveDelta < 0 ? 'text-red-400' : 'text-theme-primary') : 'text-theme-primary'} flex flex-col items-center`} >
                        <div className="flex items-center gap-1">
                          <span>{formattedValue}</span>
                        </div>
                        {(value?.appendString && value.appendString != "%") && <div style={{ color: value?.color == 'green' ? 'green' : 'red' }}>{value?.appendString}</div>}
                      </div>
                    )}
                  </td>;
                })}
              </tr>;
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>;
}